import type { ReactNode } from "react";
import { Input, Select } from "antd";
import { useTranslation } from "react-i18next";
import { useViewport } from "../../../../../hooks/useViewport";
import { normalizeVatInput } from "../../../../../lib/companyValidators";
import { EikLookupSuffix } from "./EikLookupSuffix";
import type { InvoiceBuyer } from "./useInvoiceBuyer";

interface Props {
  buyer: InvoiceBuyer;
  /** „error" за поле с грешка след опит за запис (ключовете на `useInvoiceBuyer`). */
  errStatus: (key: string) => "error" | undefined;
  /**
   * `document` — секцията „Клиент" на `InvoiceCreateModal`: на десктоп два реда
   * по четири (търсене · име ×2 · ЕИК, после ДДС № · град · адрес · МОЛ), за да
   * се събира фактурата на един екран; иначе търсене, ЕИК · ДДС № · град, име,
   * адрес · МОЛ. `form` — тесен модал (купувачът на фискалната фактура):
   * търсене, име, после две колони.
   */
  layout: "document" | "form";
  /** Enter в поле — записът на модала. */
  onPressEnter?: () => void;
  autoFocusSearch?: boolean;
}

/**
 * Полетата на купувача — едни и същи при издаване на фактура и при фискална
 * фактура от закриването; състоянието е в `useInvoiceBuyer`.
 */
export function InvoiceBuyerFields({
  buyer,
  errStatus,
  layout,
  onPressEnter,
  autoFocusSearch = false,
}: Props) {
  const { t } = useTranslation();
  const { isNarrow, isDesktop } = useViewport();
  const { values, setField, customerSearch, customerOptions } = buyer;

  const searchField = (
    <BuyerField label={t("invoiceCreate.fieldPickCompany")}>
      <Select
        showSearch
        autoFocus={autoFocusSearch}
        placeholder={t("invoiceCreate.fieldPickCompanyPh")}
        className="w-full"
        value={buyer.pickedCustomerId ?? undefined}
        onSearch={buyer.setCustomerSearch}
        onSelect={(id) => {
          const customer = customerOptions.find((option) => option.id === id);
          if (customer) buyer.fillFromCustomer(customer);
        }}
        filterOption={false}
        notFoundContent={
          customerSearch
            ? t("invoiceCreate.noCustomersForQuery")
            : t("invoiceCreate.noCustomers")
        }
        allowClear
        onClear={buyer.clearPickedCustomer}
        options={customerOptions.map((customer) => ({
          value: customer.id,
          label: `${customer.company_name} · ЕИК ${customer.eik}`,
        }))}
      />
    </BuyerField>
  );

  const nameField = (
    <BuyerField label={t("invoiceCreate.fieldName")} required>
      <Input
        status={errStatus("customer_name")}
        value={values.name}
        maxLength={255}
        placeholder={t("invoiceCreate.phCustomerName")}
        onChange={(e) => setField("name", e.target.value)}
        onPressEnter={onPressEnter}
      />
    </BuyerField>
  );

  const eikField = (
    <BuyerField label={t("invoiceCreate.fieldCustomerEik")} required>
      <Input
        status={errStatus("customer_eik")}
        value={values.eik}
        maxLength={13}
        inputMode="numeric"
        placeholder={t("invoiceCreate.phCustomerEik")}
        onChange={(e) => setField("eik", e.target.value.replace(/\D/g, "").slice(0, 13))}
        onPressEnter={onPressEnter}
        suffix={<EikLookupSuffix eik={values.eik} onLookedUp={buyer.fillFromLookup} />}
      />
    </BuyerField>
  );

  const vatField = (
    <BuyerField label={t("invoiceCreate.fieldVat")}>
      <Input
        status={errStatus("customer_vat")}
        value={values.vat}
        maxLength={20}
        placeholder="BG123456789"
        onChange={(e) => setField("vat", normalizeVatInput(e.target.value))}
        onPressEnter={onPressEnter}
      />
    </BuyerField>
  );

  const cityField = (
    <BuyerField label={t("invoiceCreate.fieldCity")} required>
      <Input
        status={errStatus("customer_city")}
        value={values.city}
        maxLength={255}
        placeholder={t("invoiceCreate.phCustomerCity")}
        onChange={(e) => setField("city", e.target.value)}
        onPressEnter={onPressEnter}
      />
    </BuyerField>
  );

  const addressField = (
    <BuyerField label={t("invoiceCreate.fieldAddress")} required>
      <Input
        status={errStatus("customer_address")}
        value={values.address}
        maxLength={500}
        placeholder={t("invoiceCreate.phCustomerAddress")}
        onChange={(e) => setField("address", e.target.value)}
        onPressEnter={onPressEnter}
      />
    </BuyerField>
  );

  const molField = (
    <BuyerField label={t("invoiceCreate.fieldMol")} hint={t("invoiceCreate.optional")}>
      <Input
        value={values.mol}
        maxLength={255}
        placeholder={t("invoiceCreate.phCustomerMol")}
        onChange={(e) => setField("mol", e.target.value)}
        onPressEnter={onPressEnter}
      />
    </BuyerField>
  );

  if (layout === "form") {
    return (
      <>
        {searchField}
        {nameField}
        <div className={`grid gap-2.5 ${isNarrow ? "grid-cols-1" : "grid-cols-2"}`}>
          {eikField}
          {vatField}
          {cityField}
          {molField}
        </div>
        {addressField}
      </>
    );
  }

  if (isDesktop) {
    return (
      <div className="grid grid-cols-4 items-end gap-x-2.5 gap-y-2">
        {searchField}
        <div className="col-span-2">{nameField}</div>
        {eikField}
        {vatField}
        {cityField}
        {addressField}
        {molField}
      </div>
    );
  }

  return (
    <>
      {searchField}
      <div className={`grid gap-2.5 ${isNarrow ? "grid-cols-1" : "grid-cols-3"}`}>
        {eikField}
        {vatField}
        {cityField}
      </div>
      {nameField}
      <div className={`grid gap-2.5 ${isNarrow ? "grid-cols-1" : "grid-cols-2"}`}>
        {addressField}
        {molField}
      </div>
    </>
  );
}

function BuyerField({
  label,
  hint,
  required,
  children,
}: {
  label: string;
  hint?: string;
  required?: boolean;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-[5px]">
      <span className="text-[11px] font-semibold tracking-[.2px] text-pos-soft">
        {label}
        {required && <span className="ml-[3px] text-pos-danger-ink">*</span>}
        {hint && <span className="ml-1.5 font-medium text-pos-muted">({hint})</span>}
      </span>
      {children}
    </div>
  );
}
